import * as React from "react";
import { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { Form, Formik } from "formik";
import { ConfigurationsActions, ConfigurationsStore } from "stores/configurations/ConfigurationsStore";
import { useStore } from "stores/connect";
import { getConfig } from "components/configurations/helpers";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import { Button, Col, Modal, Row } from "components/bootstrap";
import FormikInput from "components/common/FormikInput";
import Spinner from "components/common/Spinner";
import { InputDescription, ModalSubmit, IfPermitted, ISODurationInput } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
const StyledDefList = styled.dl.attrs({ className: "deflist" })(
  ({ theme }) => css`
    &&.deflist {
      dd {
        padding-left: ${theme.spacings.md};
        margin-left: 400px;
      }
    }
  `
);
const LabelSpan = styled.span(
  ({ theme }) => css`
    margin-left: ${theme.spacings.sm};
    font-weight: bold;
  `
);
const UserConfig = () => {
  const [showModal, setShowModal] = useState(false);
  const [viewConfig, setViewConfig] = useState(void 0);
  const [formConfig, setFormConfig] = useState(void 0);
  const configuration = useStore(ConfigurationsStore, (state) => state == null ? void 0 : state.configuration);
  const sendTelemetry = useSendTelemetry("user");
  useEffect(() => {
    ConfigurationsActions.listUserConfig(ConfigurationType.USER_CONFIG).then(() => {
      const config = getConfig(ConfigurationType.USER_CONFIG, configuration);
      setViewConfig(config);
      setFormConfig({ ...config, restrict_access_token_to_admins: !(config == null ? void 0 : config.restrict_access_token_to_admins) });
    });
  }, [configuration]);
  const saveConfig = (values) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.CONFIGURATIONS.USER_UPDATED, {
      app_action_value: "configuration-save"
    });
    const newConfig = { ...values, restrict_access_token_to_admins: !(values == null ? void 0 : values.restrict_access_token_to_admins) };
    ConfigurationsActions.update(ConfigurationType.USER_CONFIG, newConfig).then(() => {
      setShowModal(false);
    });
  };
  const resetConfig = () => {
    setShowModal(false);
    setFormConfig(viewConfig);
  };
  const timeoutIntervalValidator = (milliseconds) => milliseconds >= 1e3;
  const defaultTokenTtlValidator = (milliseconds) => milliseconds >= 864e5;
  const modalTitle = "Update User Configuration";
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, "Users Configuration"), /* @__PURE__ */ React.createElement("p", null, "These settings can be used to set a global session timeout value."), !viewConfig ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledDefList, null, /* @__PURE__ */ React.createElement("dt", null, "Global session timeout:"), /* @__PURE__ */ React.createElement("dd", null, viewConfig.enable_global_session_timeout ? "Enabled" : "Disabled"), /* @__PURE__ */ React.createElement("dt", null, "Timeout interval:"), /* @__PURE__ */ React.createElement("dd", null, viewConfig.enable_global_session_timeout ? viewConfig.global_session_timeout_interval : "-"), /* @__PURE__ */ React.createElement("dt", null, "Allow users to create personal access tokens:\xA0"), /* @__PURE__ */ React.createElement("dd", null, !viewConfig.restrict_access_token_to_admins ? "Enabled" : "Disabled"), /* @__PURE__ */ React.createElement("dt", null, "Allow access token for external users:\xA0"), /* @__PURE__ */ React.createElement("dd", null, viewConfig.allow_access_token_for_external_user ? "Enabled" : "Disabled"), /* @__PURE__ */ React.createElement("dt", null, "Default TTL for new tokens:"), /* @__PURE__ */ React.createElement("dd", null, viewConfig.default_ttl_for_new_tokens ? viewConfig.default_ttl_for_new_tokens : "-")), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
    Button,
    {
      type: "button",
      bsSize: "xs",
      bsStyle: "info",
      onClick: () => {
        setShowModal(true);
      }
    },
    "Edit configuration"
  ))), /* @__PURE__ */ React.createElement(Modal, { show: showModal && !!formConfig, onHide: resetConfig }, /* @__PURE__ */ React.createElement(Formik, { onSubmit: saveConfig, initialValues: formConfig }, ({ isSubmitting, values, setFieldValue }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, modalTitle)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "enable_global_session_timeout",
      id: "enable_global_session_timeout",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Enable global session timeout")
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { help: "If enabled, it will be set for all the users." })), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(
    ISODurationInput,
    {
      id: "global_session_timeout_interval",
      duration: values.global_session_timeout_interval,
      update: (value) => setFieldValue("global_session_timeout_interval", value),
      label: "Global session timeout interval (as ISO8601 Duration)",
      help: "Session automatically end after this amount of time, unless they are actively used. Example, for 60 seconds: PT60S, for 60 minutes: PT60M",
      validator: timeoutIntervalValidator,
      errorText: "invalid (min: 1 second)",
      disabled: !values.enable_global_session_timeout,
      required: true
    }
  ))), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "restrict_access_token_to_admins",
      id: "restrict_access_token_to_admins",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Allow users to create personal access tokens")
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { help: "If enabled, it will allow users to create access tokens." })), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "allow_access_token_for_external_user",
      id: "allow_access_token_for_external_user",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Allow access token for external users")
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { help: "If enabled, it will allow external users to create access tokens." })), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(
    ISODurationInput,
    {
      id: "default_ttl_for_new_tokens",
      duration: values.default_ttl_for_new_tokens,
      update: (value) => setFieldValue("default_ttl_for_new_tokens", value),
      label: "Default TTL for new tokens (as ISO8601 Duration)",
      help: "Tokens will be automatically invalidated after this amount of time. Example, for 24 hours: PT24H, for 30 days: P30D",
      validator: defaultTokenTtlValidator,
      errorText: "invalid (min: 1 day)",
      disabled: !values.default_ttl_for_new_tokens,
      required: true
    }
  )))))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      onCancel: resetConfig,
      isSubmitting,
      isAsyncSubmit: true,
      submitLoadingText: "Update configuration",
      submitButtonText: "Update configuration"
    }
  )))))));
};
export default UserConfig;
